import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import TabNavigation from './components/TabNavigation';
import DataGridToolbar from './components/DataGridToolbar';
import FixedExpensesGrid from './components/FixedExpensesGrid';
import VariableExpensesGrid from './components/VariableExpensesGrid';
import MaterialsGrid from './components/MaterialsGrid';
import EngineersGrid from './components/EngineersGrid';
import SoftwareLicensesGrid from './components/SoftwareLicensesGrid';
import VendorsGrid from './components/VendorsGrid';
import OutsourceResourcesGrid from './components/OutsourceResourcesGrid';
import ExportImportModal from './components/ExportImportModal';
import AIPriceFetchModal from './components/AIPriceFetchModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { DollarSign } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getFixedExpenses, createFixedExpense, updateFixedExpense, deleteFixedExpense,
  restoreFixedExpense, permanentlyDeleteFixedExpense, getDeletedFixedExpenses,
  getExpenseGroups, createExpenseGroup, deleteExpenseGroup,
  getVariableExpenses, createVariableExpense, updateVariableExpense, deleteVariableExpense,
  restoreVariableExpense, permanentlyDeleteVariableExpense, getDeletedVariableExpenses,
  getMaterials, createMaterial, updateMaterial, deleteMaterial,
  restoreMaterial, permanentlyDeleteMaterial, getDeletedMaterials,
  getEngineers, createEngineer, updateEngineer, deleteEngineer,
  restoreEngineer, permanentlyDeleteEngineer, getDeletedEngineers,
  getSoftwareLicenses, createSoftwareLicense, updateSoftwareLicense, deleteSoftwareLicense,
  restoreSoftwareLicense, permanentlyDeleteSoftwareLicense, getDeletedSoftwareLicenses,
  getVendors, createVendor, updateVendor, deleteVendor,
  restoreVendor, permanentlyDeleteVendor, getDeletedVendors,
  getOutsourceResources, createOutsourceResource, updateOutsourceResource, deleteOutsourceResource,
  restoreOutsourceResource, permanentlyDeleteOutsourceResource, getDeletedOutsourceResources,
} from '../../services/masterDataService';
import { SUPPORTED_CURRENCIES, convertToOMR, getExchangeRates } from '../../services/exchangeRateService';
import RecycleBinModal from './components/RecycleBinModal';


const CURRENCIES = [
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
];

const getDefaultRecord = (tab) => {
  switch (tab) {
    case 'fixed-expenses':
      return { name: '', category: 'office', monthlyCost: '', description: '', groupName: '' };
    case 'variable-expenses':
      return { name: '', category: 'operational', monthlyCost: '', description: '' };
    case 'materials':
      return { itemCode: '', itemName: '', category: 'fiber', uom: 'meter', unitCost: '', wastePercentage: '', currency: 'OMR', unitCostOriginal: '', unitCostOmr: '', exchangeRate: 1, exchangeRateDate: new Date()?.toISOString()?.split('T')?.[0] };
    case 'engineers':
      return { name: '', role: 'senior', specialization: '', monthlySalary: '', internalCost: '', utilizationDefault: '', nationality: 'expat', spfPercentage: 22.5, healthInsuranceEnabled: false, healthInsuranceValue: 0 };
    case 'software':
      return { softwareName: '', vendor: '', category: 'design', licenseType: 'annual', cost: '', numberOfLicenses: '', renewalDate: '' };
    case 'vendors':
      return { vendorName: '', contactPerson: '', email: '', phone: '', category: 'fiber', rating: 3, paymentTerms: 'net30' };
    case 'outsource':
      return { name: '', role: 'senior', specialization: '', monthlySalary: '', dailySalary: 0, hourlySalary: 0, internalCost: 0, utilizationDefault: 85 };
    default:
      return {};
  }
};

const getFieldDefs = (tab) => {
  switch (tab) {
    case 'fixed-expenses':
      return [
        { key: 'name', label: 'Expense Name', type: 'text', required: true },
        { key: 'category', label: 'Category', type: 'select', options: ['office', 'utilities', 'insurance', 'admin', 'other'], required: true },
        { key: 'monthlyCost', label: 'Monthly Cost (OMR)', type: 'number', required: true },
        { key: 'description', label: 'Description', type: 'text' },
        { key: 'groupName', label: 'Group (optional)', type: 'expense-group-select' },
      ];
    case 'variable-expenses':
      return [
        { key: 'name', label: 'Expense Name', type: 'text', required: true },
        { key: 'category', label: 'Category', type: 'select', options: ['operational','travel','communication','maintenance','marketing','other'], required: true },
        { key: 'monthlyCost', label: 'Monthly Cost (OMR)', type: 'number', required: true },
        { key: 'description', label: 'Description', type: 'text' },
      ];
    case 'materials':
      return [
        { key: 'itemCode', label: 'Item Code', type: 'text', required: true },
        { key: 'itemName', label: 'Item Name', type: 'text', required: true },
        { key: 'category', label: 'Category', type: 'select', options: ['fiber','cable','hardware','equipment','consumables'], required: true },
        { key: 'uom', label: 'Unit of Measure', type: 'select', options: ['meter','piece','unit','box','roll'], required: true },
        { key: 'wastePercentage', label: 'Waste %', type: 'number' },
      ];
    case 'engineers':
      return [
        { key: 'name', label: 'Full Name', type: 'text', required: true },
        { key: 'role', label: 'Role', type: 'select', options: ['senior','mid','junior','lead','architect'], required: true },
        { key: 'specialization', label: 'Specialization', type: 'text', required: true },
        { key: 'monthlySalary', label: 'Monthly Salary (OMR)', type: 'number', required: true },
        { key: 'dailySalary', label: 'Daily Salary (OMR) — auto', type: 'number', readOnly: true },
        { key: 'hourlySalary', label: 'Hourly Salary (OMR) — auto', type: 'number', readOnly: true },
        { key: 'utilizationDefault', label: 'Default Utilization %', type: 'number' },
      ];
    case 'software':
      return [
        { key: 'softwareName', label: 'Software Name', type: 'text', required: true },
        { key: 'vendor', label: 'Vendor', type: 'text', required: true },
        { key: 'category', label: 'Category', type: 'select', options: ['design','project','development','productivity','other'], required: true },
        { key: 'licenseType', label: 'License Type', type: 'select', options: ['annual','monthly','perpetual'], required: true },
        { key: 'cost', label: 'Cost (OMR)', type: 'number', required: true },
        { key: 'numberOfLicenses', label: 'Number of Licenses', type: 'number', required: true },
        { key: 'renewalDate', label: 'Renewal Date', type: 'date' },
      ];
    case 'vendors':
      return [
        { key: 'vendorName', label: 'Vendor Name', type: 'text', required: true },
        { key: 'contactPerson', label: 'Contact Person', type: 'text', required: true },
        { key: 'email', label: 'Email', type: 'email', required: true },
        { key: 'phone', label: 'Phone', type: 'text' },
        { key: 'category', label: 'Category', type: 'select', options: ['fiber','hardware','civil','testing','services','other'], required: true },
        { key: 'rating', label: 'Rating (1-5)', type: 'number' },
        { key: 'paymentTerms', label: 'Payment Terms', type: 'select', options: ['net30','net45','net60','advance','cod'] },
      ];
    case 'outsource':
      return [
        { key: 'name', label: 'Full Name', type: 'text', required: true },
        { key: 'role', label: 'Role', type: 'select', options: ['senior','mid','junior','lead','architect'], required: true },
        { key: 'specialization', label: 'Specialization', type: 'text', required: true },
        { key: 'monthlySalary', label: 'Monthly Salary (OMR)', type: 'number', required: true },
        { key: 'dailySalary', label: 'Daily Salary (OMR) — auto', type: 'number', readOnly: true },
        { key: 'hourlySalary', label: 'Hourly Salary (OMR) — auto', type: 'number', readOnly: true },
        { key: 'utilizationDefault', label: 'Default Utilization %', type: 'number' },
      ];
    default:
      return [];
  }
};

const TAB_LABELS = {
  'fixed-expenses': 'Fixed Expense',
  'variable-expenses': 'Variable Expense',
  'materials': 'Material',
  'engineers': 'Engineer',
  'software': 'Software License',
  'vendors': 'Vendor',
  'outsource': 'Outsource Resource',
};

const MasterDataManagement = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(() => {
    const tabParam = searchParams?.get('tab');
    const validTabs = ['fixed-expenses', 'variable-expenses', 'materials', 'engineers', 'software', 'vendors', 'outsource'];
    return validTabs?.includes(tabParam) ? tabParam : 'fixed-expenses';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [exportImportModal, setExportImportModal] = useState({ isOpen: false, mode: 'export' });
  const [aiPriceFetchModal, setAIPriceFetchModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('OMR');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({});
  const [addFormErrors, setAddFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [materialConverting, setMaterialConverting] = useState(false);
  const [materialRateInfo, setMaterialRateInfo] = useState({ date: '', source: '' });

  // Recycle bin state
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);
  const [deletedItems, setDeletedItems] = useState([]);
  const [recycleBinLoading, setRecycleBinLoading] = useState(false);

  // Outsource auto-save state
  const outsourceGridRef = useRef(null);
  const [outsourceUnsavedCount, setOutsourceUnsavedCount] = useState(0);
  const [globalSaving, setGlobalSaving] = useState(false);

  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [expenseGroups, setExpenseGroups] = useState([]);
  const [variableExpenses, setVariableExpenses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [softwareLicenses, setSoftwareLicenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [outsourceResources, setOutsourceResources] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});

  // Ref so handleUpdate always reads the current activeTab even inside debounce timers
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next?.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const loadTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);
    try {
      switch (tab) {
        case 'fixed-expenses':
          const [expenses, grps] = await Promise.all([getFixedExpenses(), getExpenseGroups()]);
          setFixedExpenses(expenses);
          setExpenseGroups(grps);
          break;
        case 'variable-expenses': setVariableExpenses(await getVariableExpenses()); break;
        case 'materials': setMaterials(await getMaterials()); break;
        case 'engineers': setEngineers(await getEngineers()); break;
        case 'software': setSoftwareLicenses(await getSoftwareLicenses()); break;
        case 'vendors': setVendors(await getVendors()); break;
        case 'outsource': setOutsourceResources(await getOutsourceResources()); break;
        default: break;
      }
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const tabs = [
    { id: 'fixed-expenses', label: 'Fixed Expenses', shortLabel: 'Fixed', icon: 'DollarSign', count: fixedExpenses?.length },
    { id: 'variable-expenses', label: 'Variable Expenses', shortLabel: 'Variable', icon: 'TrendingUp', count: variableExpenses?.length },
    { id: 'materials', label: 'Materials', icon: 'Package', count: materials?.length },
    { id: 'engineers', label: 'Engineers Pool', shortLabel: 'Engineers', icon: 'Users', count: engineers?.length },
    { id: 'software', label: 'Software & Licenses', shortLabel: 'Software', icon: 'Laptop', count: softwareLicenses?.length },
    { id: 'vendors', label: 'Vendors', icon: 'Building2', count: vendors?.length },
    { id: 'outsource', label: 'Outsource Resources', shortLabel: 'Outsource', icon: 'UserCheck', count: outsourceResources?.length },
  ];

  const getCurrentData = () => {
    switch (activeTab) {
      case 'fixed-expenses': return fixedExpenses;
      case 'variable-expenses': return variableExpenses;
      case 'materials': return materials;
      case 'engineers': return engineers;
      case 'software': return softwareLicenses;
      case 'vendors': return vendors;
      case 'outsource': return outsourceResources;
      default: return [];
    }
  };

  const handleUpdate = useCallback(async (updatedItem) => {
    try {
      switch (activeTabRef?.current) {
        case 'fixed-expenses': {
          const updated = await updateFixedExpense(updatedItem?.id, updatedItem);
          setFixedExpenses(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        case 'variable-expenses': {
          const updated = await updateVariableExpense(updatedItem?.id, updatedItem);
          setVariableExpenses(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        case 'materials': {
          const updated = await updateMaterial(updatedItem?.id, updatedItem);
          setMaterials(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        case 'engineers': {
          const updated = await updateEngineer(updatedItem?.id, updatedItem);
          setEngineers(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        case 'software': {
          const updated = await updateSoftwareLicense(updatedItem?.id, updatedItem);
          setSoftwareLicenses(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        case 'vendors': {
          const updated = await updateVendor(updatedItem?.id, updatedItem);
          setVendors(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        case 'outsource': {
          const updated = await updateOutsourceResource(updatedItem?.id, updatedItem);
          setOutsourceResources(prev => prev?.map(item => item?.id === updated?.id ? updated : item));
          break;
        }
        default: break;
      }
    } catch (err) {
      setError(err?.message || 'Failed to update record');
    }
  }, [activeTabRef, setFixedExpenses, setVariableExpenses, setMaterials, setEngineers, setSoftwareLicenses, setVendors, setOutsourceResources, setError]);

  const handleDelete = async (id) => {
    try {
      const userEmail = user?.email || null;
      switch (activeTab) {
        case 'fixed-expenses':
          await deleteFixedExpense(id, userEmail);
          setFixedExpenses(prev => prev?.filter(item => item?.id !== id));
          break;
        case 'variable-expenses':
          await deleteVariableExpense(id, userEmail);
          setVariableExpenses(prev => prev?.filter(item => item?.id !== id));
          break;
        case 'materials':
          await deleteMaterial(id, userEmail);
          setMaterials(prev => prev?.filter(item => item?.id !== id));
          break;
        case 'engineers':
          await deleteEngineer(id, userEmail);
          setEngineers(prev => prev?.filter(item => item?.id !== id));
          break;
        case 'software':
          await deleteSoftwareLicense(id, userEmail);
          setSoftwareLicenses(prev => prev?.filter(item => item?.id !== id));
          break;
        case 'vendors':
          await deleteVendor(id, userEmail);
          setVendors(prev => prev?.filter(item => item?.id !== id));
          break;
        case 'outsource':
          await deleteOutsourceResource(id, userEmail);
          setOutsourceResources(prev => prev?.filter(item => item?.id !== id));
          break;
        default: break;
      }
    } catch (err) {
      setError(err?.message || 'Failed to delete record');
    }
  };

  const handleOpenRecycleBin = async () => {
    setRecycleBinOpen(true);
    setRecycleBinLoading(true);
    try {
      let items = [];
      switch (activeTab) {
        case 'fixed-expenses': items = await getDeletedFixedExpenses(); break;
        case 'variable-expenses': items = await getDeletedVariableExpenses(); break;
        case 'materials': items = await getDeletedMaterials(); break;
        case 'engineers': items = await getDeletedEngineers(); break;
        case 'software': items = await getDeletedSoftwareLicenses(); break;
        case 'vendors': items = await getDeletedVendors(); break;
        case 'outsource': items = await getDeletedOutsourceResources(); break;
        default: items = [];
      }
      setDeletedItems(items);
    } catch (err) {
      setError(err?.message || 'Failed to load recycle bin');
    } finally {
      setRecycleBinLoading(false);
    }
  };

  const handleRestoreItem = async (id) => {
    try {
      let restored = null;
      switch (activeTab) {
        case 'fixed-expenses':
          restored = await restoreFixedExpense(id);
          setFixedExpenses(prev => [...prev, restored]);
          break;
        case 'variable-expenses':
          restored = await restoreVariableExpense(id);
          setVariableExpenses(prev => [...prev, restored]);
          break;
        case 'materials':
          restored = await restoreMaterial(id);
          setMaterials(prev => [...prev, restored]);
          break;
        case 'engineers':
          restored = await restoreEngineer(id);
          setEngineers(prev => [...prev, restored]);
          break;
        case 'software':
          restored = await restoreSoftwareLicense(id);
          setSoftwareLicenses(prev => [...prev, restored]);
          break;
        case 'vendors':
          restored = await restoreVendor(id);
          setVendors(prev => [...prev, restored]);
          break;
        case 'outsource':
          restored = await restoreOutsourceResource(id);
          setOutsourceResources(prev => [...prev, restored]);
          break;
        default: break;
      }
      setDeletedItems(prev => prev?.filter(item => item?.id !== id));
    } catch (err) {
      setError(err?.message || 'Failed to restore record');
    }
  };

  const handlePermanentDelete = async (id) => {
    try {
      switch (activeTab) {
        case 'fixed-expenses': await permanentlyDeleteFixedExpense(id); break;
        case 'variable-expenses': await permanentlyDeleteVariableExpense(id); break;
        case 'materials': await permanentlyDeleteMaterial(id); break;
        case 'engineers': await permanentlyDeleteEngineer(id); break;
        case 'software': await permanentlyDeleteSoftwareLicense(id); break;
        case 'vendors': await permanentlyDeleteVendor(id); break;
        case 'outsource': await permanentlyDeleteOutsourceResource(id); break;
        default: break;
      }
      setDeletedItems(prev => prev?.filter(item => item?.id !== id));
    } catch (err) {
      setError(err?.message || 'Failed to permanently delete record');
    }
  };

  const handleAddNew = () => {
    setAddFormData(getDefaultRecord(activeTab));
    setAddFormErrors({});
    setMaterialRateInfo({ date: '', source: '' });
    setAddModalOpen(true);
    // Pre-load exchange rate info for materials
    if (activeTab === 'materials') {
      getExchangeRates()?.then(({ date, source }) => {
        setMaterialRateInfo({ date, source });
      })?.catch(() => {});
    }
  };

  const handleAddFormChange = (key, value) => {
    if ((activeTab === 'engineers' || activeTab === 'outsource') && key === 'monthlySalary') {
      const monthly = parseFloat(value) || 0;
      const daily = monthly > 0 ? parseFloat((monthly / 30)?.toFixed(6)) : 0;
      const hourly = daily > 0 ? parseFloat((daily / 9)?.toFixed(6)) : 0;
      setAddFormData(prev => ({
        ...prev,
        monthlySalary: value,
        dailySalary: daily,
        hourlySalary: hourly,
        internalCost: daily,
      }));
    } else {
      setAddFormData(prev => ({ ...prev, [key]: value }));
    }
    setAddFormErrors(prev => ({ ...prev, [key]: '' }));
  };

  // Handle currency or cost change for materials with real-time OMR conversion
  const handleMaterialCurrencyOrCostChange = async (newCurrency, newCost) => {
    const currency = newCurrency ?? addFormData?.currency ?? 'OMR';
    const originalCost = parseFloat(newCost ?? addFormData?.unitCostOriginal) || 0;
    const today = new Date()?.toISOString()?.split('T')?.[0];

    setAddFormData(prev => ({
      ...prev,
      currency,
      unitCostOriginal: newCost !== undefined ? newCost : prev?.unitCostOriginal,
    }));

    if (currency === 'OMR') {
      setAddFormData(prev => ({
        ...prev,
        currency: 'OMR',
        unitCostOriginal: originalCost,
        unitCost: originalCost,
        unitCostOmr: originalCost,
        exchangeRate: 1,
        exchangeRateDate: today,
      }));
      return;
    }

    if (!originalCost) return;

    setMaterialConverting(true);
    try {
      const { omrAmount, rate, date, source } = await convertToOMR(originalCost, currency);
      setAddFormData(prev => ({
        ...prev,
        currency,
        unitCostOriginal: originalCost,
        unitCost: omrAmount,
        unitCostOmr: omrAmount,
        exchangeRate: rate,
        exchangeRateDate: date,
      }));
      setMaterialRateInfo({ date, source });
    } catch (_) {
      // keep previous values
    } finally {
      setMaterialConverting(false);
    }
  };

  const validateAddForm = () => {
    const fields = getFieldDefs(activeTab);
    const errors = {};
    fields?.forEach(f => {
      if (f?.required && !String(addFormData?.[f?.key] ?? '')?.trim()) {
        errors[f.key] = `${f?.label} is required`;
      }
    });
    // For materials, validate unitCostOriginal
    if (activeTab === 'materials') {
      if (!addFormData?.unitCostOriginal && addFormData?.unitCostOriginal !== 0) {
        errors['unitCostOriginal'] = 'Unit Cost is required';
      }
    }
    return errors;
  };

  const handleAddSave = async () => {
    const errors = validateAddForm();
    if (Object.keys(errors)?.length > 0) {
      setAddFormErrors(errors);
      return;
    }
    let record = { ...addFormData };
    // Parse numeric fields
    getFieldDefs(activeTab)?.forEach(f => {
      if (f?.type === 'number' && record?.[f?.key] !== '') {
        record[f.key] = parseFloat(record?.[f?.key]) || 0;
      }
    });

    // For materials: ensure currency fields are consistent
    if (activeTab === 'materials') {
      const originalCost = parseFloat(record?.unitCostOriginal) || 0;
      const currency = record?.currency || 'OMR';
      if (currency === 'OMR') {
        record.unitCost = originalCost;
        record.unitCostOmr = originalCost;
        record.exchangeRate = 1;
        record.exchangeRateDate = new Date()?.toISOString()?.split('T')?.[0];
      } else {
        // unitCostOmr should already be set by handleMaterialCurrencyOrCostChange
        record.unitCost = record?.unitCostOmr ?? originalCost;
      }
      record.unitCostOriginal = originalCost;
      record.wastePercentage = parseFloat(record?.wastePercentage) || 0;
    }

    setSaving(true);
    try {
      const userId = user?.id;
      switch (activeTab) {
        case 'fixed-expenses': {
          const created = await createFixedExpense(record, userId);
          setFixedExpenses(prev => [...prev, created]);
          break;
        }
        case 'variable-expenses': {
          const created = await createVariableExpense(record, userId);
          setVariableExpenses(prev => [...prev, created]);
          break;
        }
        case 'materials': {
          const created = await createMaterial(record, userId);
          setMaterials(prev => [...prev, created]);
          break;
        }
        case 'engineers': {
          const created = await createEngineer(record, userId);
          setEngineers(prev => [...prev, created]);
          break;
        }
        case 'software': {
          const created = await createSoftwareLicense(record, userId);
          setSoftwareLicenses(prev => [...prev, created]);
          break;
        }
        case 'vendors': {
          const created = await createVendor(record, userId);
          setVendors(prev => [...prev, created]);
          break;
        }
        case 'outsource': {
          const created = await createOutsourceResource(record, userId);
          setOutsourceResources(prev => [...prev, created]);
          break;
        }
        default: break;
      }
      setAddModalOpen(false);
      setAddFormData({});
    } catch (err) {
      setError(err?.message || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleGlobalSaveAll = async () => {
    if (!outsourceGridRef?.current?.saveAll) return;
    setGlobalSaving(true);
    try {
      await outsourceGridRef?.current?.saveAll();
    } finally {
      setGlobalSaving(false);
    }
  };

  const handleBulkUpload = () => setExportImportModal({ isOpen: true, mode: 'import' });
  const handleExport = () => setExportImportModal({ isOpen: true, mode: 'export' });
  const handleAIPriceFetch = () => setAIPriceFetchModal(true);

  const handleImport = async (importedData) => {
    const userId = user?.id;
    try {
      const created = [];
      for (const item of importedData) {
        switch (activeTab) {
          case 'fixed-expenses': created?.push(await createFixedExpense(item, userId)); break;
          case 'variable-expenses': created?.push(await createVariableExpense(item, userId)); break;
          case 'materials': created?.push(await createMaterial(item, userId)); break;
          case 'engineers': created?.push(await createEngineer(item, userId)); break;
          case 'software': created?.push(await createSoftwareLicense(item, userId)); break;
          case 'vendors': created?.push(await createVendor(item, userId)); break;
          case 'outsource': created?.push(await createOutsourceResource(item, userId)); break;
          default: break;
        }
      }
      await loadTabData(activeTab);
    } catch (err) {
      setError(err?.message || 'Failed to import records');
    }
  };

  const handleApplyAIPrices = async (suggestedItems) => {
    const userId = user?.id;
    try {
      for (const item of suggestedItems) {
        switch (activeTab) {
          case 'fixed-expenses': await createFixedExpense(item, userId); break;
          case 'variable-expenses': await createVariableExpense(item, userId); break;
          case 'materials': await createMaterial(item, userId); break;
          case 'engineers': await createEngineer(item, userId); break;
          case 'software': await createSoftwareLicense(item, userId); break;
          case 'vendors': await createVendor(item, userId); break;
          case 'outsource': await createOutsourceResource(item, userId); break;
          default: break;
        }
      }
      await loadTabData(activeTab);
    } catch (err) {
      setError(err?.message || 'Failed to apply AI prices');
    }
  };

  const handleDeleteSelected = () => {};

  const handleCreateExpenseGroup = async (group) => {
    try {
      const created = await createExpenseGroup(group, user?.id);
      setExpenseGroups(prev => [...prev, created]);
    } catch (err) {
      setError(err?.message || 'Failed to create group');
    }
  };

  const handleDeleteExpenseGroup = async (groupId) => {
    try {
      await deleteExpenseGroup(groupId);
      setExpenseGroups(prev => prev?.filter(g => g?.id !== groupId));
    } catch (err) {
      setError(err?.message || 'Failed to delete group');
    }
  };

  const renderGrid = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading data...</p>
          </div>
        </div>
      );
    }
    switch (activeTab) {
      case 'fixed-expenses':
        return (
          <FixedExpensesGrid
            data={fixedExpenses}
            groups={expenseGroups}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onCreateGroup={handleCreateExpenseGroup}
            onDeleteGroup={handleDeleteExpenseGroup}
            searchQuery={searchQuery}
          />
        );
      case 'variable-expenses':
        return <VariableExpensesGrid data={variableExpenses} onUpdate={handleUpdate} onDelete={handleDelete} searchQuery={searchQuery} />;
      case 'materials':
        return <MaterialsGrid data={materials} onUpdate={handleUpdate} onDelete={handleDelete} searchQuery={searchQuery} />;
      case 'engineers':
        return <EngineersGrid data={engineers} onUpdate={handleUpdate} onDelete={handleDelete} searchQuery={searchQuery} />;
      case 'software':
        return <SoftwareLicensesGrid data={softwareLicenses} onUpdate={handleUpdate} onDelete={handleDelete} searchQuery={searchQuery} />;
      case 'vendors':
        return <VendorsGrid data={vendors} onUpdate={handleUpdate} onDelete={handleDelete} searchQuery={searchQuery} />;
      case 'outsource':
        return (
          <OutsourceResourcesGrid
            ref={outsourceGridRef}
            data={outsourceResources}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            searchQuery={searchQuery}
            onUnsavedCountChange={setOutsourceUnsavedCount}
          />
        );
      default:
        return null;
    }
  };

  const fieldDefs = getFieldDefs(activeTab);

  return (
    <MainLayout>
      <div className="space-y-4 md:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">Master Data Management</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">Configure foundational system data and settings</p>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'outsource' && (
              <button
                onClick={handleGlobalSaveAll}
                disabled={outsourceUnsavedCount === 0 || globalSaving}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all shadow-sm ${
                  outsourceUnsavedCount > 0 && !globalSaving
                    ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 cursor-pointer'
                    : 'bg-muted text-muted-foreground border-border cursor-not-allowed opacity-60'
                }`}
                title="Save all pending changes"
              >
                {globalSaving ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Saving…
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={15} />
                    Save All
                    {outsourceUnsavedCount > 0 && (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-xs font-bold leading-none">
                        {outsourceUnsavedCount}
                      </span>
                    )}
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleOpenRecycleBin}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-card hover:bg-muted text-sm text-muted-foreground hover:text-foreground transition-colors shadow-sm"
              title="Open Recycle Bin"
            >
              <Icon name="Trash2" size={15} className="text-warning" />
              <span className="hidden sm:inline font-medium">Recycle Bin</span>
            </button>
            <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2 shadow-sm">
              <DollarSign className="w-4 h-4 text-primary flex-shrink-0" />
              <div className="flex flex-col">
                <label className="text-xs text-muted-foreground font-medium leading-none mb-1">Currency</label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e?.target?.value)}
                  className="bg-transparent text-sm font-semibold text-foreground border-none outline-none cursor-pointer pr-1"
                >
                  {CURRENCIES?.map((c) => (
                    <option key={c?.code} value={c?.code}>{c?.code} — {c?.name} ({c?.symbol})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
            <Icon name="AlertCircle" size={16} />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
          </div>
        )}

        <div className="bg-gradient-to-br from-card via-card to-muted/10 rounded-2xl border border-border shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-smooth">
          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />
          <DataGridToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onAddNew={handleAddNew}
            onBulkUpload={handleBulkUpload}
            onExport={handleExport}
            onAIPriceFetch={handleAIPriceFetch}
            onDeleteSelected={handleDeleteSelected}
            selectedCount={0}
            addLabel={`Add ${TAB_LABELS?.[activeTab] || 'Record'}`}
          />
          <div className="bg-background">{renderGrid()}</div>
        </div>
      </div>
      {/* Add New Record Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Plus" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">Add {TAB_LABELS?.[activeTab]}</h2>
                  <p className="text-xs text-muted-foreground">Fill in the details below</p>
                </div>
              </div>
              <button onClick={() => setAddModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors">
                <Icon name="X" size={16} className="text-muted-foreground" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {fieldDefs?.map(field => (
                <div key={field?.key} className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">
                    {field?.label}
                    {field?.required && <span className="text-error ml-1">*</span>}
                  </label>
                  {field?.type === 'select' ? (
                    <select
                      value={addFormData?.[field?.key] ?? ''}
                      onChange={e => handleAddFormChange(field?.key, e?.target?.value)}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors capitalize"
                    >
                      {field?.options?.map(opt => (
                        <option key={opt} value={opt} className="capitalize">{opt}</option>
                      ))}
                    </select>
                  ) : field?.type === 'expense-group-select' ? (
                    <select
                      value={addFormData?.[field?.key] ?? ''}
                      onChange={e => handleAddFormChange(field?.key, e?.target?.value || null)}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                    >
                      <option value="">— No Group (Standalone) —</option>
                      {expenseGroups?.map(g => (
                        <option key={g?.id} value={g?.name}>{g?.name}</option>
                      ))}
                    </select>
                  ) : field?.readOnly ? (
                    <div className="w-full h-10 px-3 rounded-lg border border-border bg-muted text-sm text-primary font-medium flex items-center">
                      {addFormData?.[field?.key]
                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'OMR', minimumFractionDigits: 3, maximumFractionDigits: 3 })?.format(addFormData?.[field?.key])
                        : <span className="text-muted-foreground italic">Auto-calculated</span>}
                    </div>
                  ) : (
                    <input
                      type={field?.type}
                      value={addFormData?.[field?.key] ?? ''}
                      onChange={e => handleAddFormChange(field?.key, e?.target?.value)}
                      className={`w-full h-10 px-3 rounded-lg border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors ${addFormErrors?.[field?.key] ? 'border-error' : 'border-border'}`}
                      placeholder={`Enter ${field?.label?.toLowerCase()}`}
                    />
                  )}
                  {addFormErrors?.[field?.key] && (
                    <p className="text-xs text-error">{addFormErrors?.[field?.key]}</p>
                  )}
                </div>
              ))}

              {/* Currency + Unit Cost block for Materials */}
              {activeTab === 'materials' && (
                <div className="space-y-3 p-4 bg-muted/30 border border-border rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="DollarSign" size={14} className="text-primary" />
                    <span className="text-sm font-semibold text-foreground">Pricing & Currency</span>
                  </div>

                  {/* Exchange rate info banner */}
                  {materialRateInfo?.date && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <Icon name="RefreshCw" size={12} className="text-blue-500 flex-shrink-0" />
                      <span className="text-xs text-blue-700 dark:text-blue-300">
                        Rates as of <strong>{materialRateInfo?.date}</strong>
                        {materialRateInfo?.source === 'live' && ' (live)'}
                        {materialRateInfo?.source === 'cache' && ' (cached today)'}
                        {materialRateInfo?.source === 'fallback' && ' (approximate)'}
                        {' '}· Locked per transaction.
                      </span>
                    </div>
                  )}

                  {/* Currency selector */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-foreground">Currency</label>
                    <select
                      value={addFormData?.currency || 'OMR'}
                      onChange={e => handleMaterialCurrencyOrCostChange(e?.target?.value, addFormData?.unitCostOriginal)}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                    >
                      {SUPPORTED_CURRENCIES?.map(c => (
                        <option key={c?.value} value={c?.value}>{c?.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Unit Cost input */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-foreground">
                      Unit Cost ({addFormData?.currency || 'OMR'})
                      <span className="text-error ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={addFormData?.unitCostOriginal ?? ''}
                        onChange={e => handleMaterialCurrencyOrCostChange(addFormData?.currency, e?.target?.value)}
                        className={`w-full h-10 px-3 rounded-lg border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors pr-20 ${addFormErrors?.unitCostOriginal ? 'border-error' : 'border-border'}`}
                        placeholder={`Enter cost in ${addFormData?.currency || 'OMR'}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground">
                        {addFormData?.currency || 'OMR'}
                      </span>
                    </div>
                    {addFormErrors?.unitCostOriginal && (
                      <p className="text-xs text-error">{addFormErrors?.unitCostOriginal}</p>
                    )}
                  </div>

                  {/* OMR conversion display */}
                  {(addFormData?.currency && addFormData?.currency !== 'OMR') && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-foreground">Converted to OMR</label>
                      <div className="w-full h-10 px-3 rounded-lg border border-primary/30 bg-primary/5 text-sm font-semibold text-primary flex items-center justify-between">
                        {materialConverting ? (
                          <span className="flex items-center gap-2 text-muted-foreground font-normal">
                            <Icon name="Loader2" size={14} className="animate-spin" />
                            Converting...
                          </span>
                        ) : addFormData?.unitCostOmr ? (
                          <>
                            <span>OMR {parseFloat(addFormData?.unitCostOmr || 0)?.toFixed(3)}</span>
                            {addFormData?.exchangeRate && addFormData?.exchangeRate !== 1 && (
                              <span className="text-xs text-muted-foreground font-normal">
                                Rate: {parseFloat(addFormData?.exchangeRate || 1)?.toFixed(6)} · {addFormData?.exchangeRateDate}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground font-normal italic">Enter cost above to convert</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* OMR display when currency is OMR */}
                  {(!addFormData?.currency || addFormData?.currency === 'OMR') && addFormData?.unitCostOriginal && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="CheckCircle" size={12} className="text-success" />
                      <span>Stored directly as OMR {parseFloat(addFormData?.unitCostOriginal || 0)?.toFixed(3)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setAddModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleAddSave} iconName={saving ? undefined : 'Plus'} iconPosition="left" disabled={saving}>
                {saving ? 'Saving...' : `Add ${TAB_LABELS?.[activeTab]}`}
              </Button>
            </div>
          </div>
        </div>
      )}
      <RecycleBinModal
        isOpen={recycleBinOpen}
        onClose={() => setRecycleBinOpen(false)}
        activeTab={activeTab}
        deletedItems={deletedItems}
        loading={recycleBinLoading}
        onRestore={handleRestoreItem}
        onPermanentDelete={handlePermanentDelete}
      />
      <ExportImportModal
        isOpen={exportImportModal?.isOpen}
        onClose={() => setExportImportModal({ isOpen: false, mode: 'export' })}
        mode={exportImportModal?.mode}
        data={getCurrentData()}
        dataType={activeTab}
        onImport={handleImport}
      />
      <AIPriceFetchModal
        isOpen={aiPriceFetchModal}
        onClose={() => setAIPriceFetchModal(false)}
        dataType={activeTab}
        onApplyPrices={handleApplyAIPrices}
      />
    </MainLayout>
  );
};

export default MasterDataManagement;